<template>
  <div class="payload-mapping">
    <label class="label">{{ $t('apiChannel.payloadMapping') }}</label>

    <!-- Add new field row -->
    <div class="payload-field-row field is-grouped">
      <p class="control" style="width: 20%">
        <input
          v-model="newField.key"
          class="input is-small"
          type="text"
          :placeholder="$t('key')"
        >
      </p>
      <p class="control" style="width: 15%">
        <span class="select is-small is-fullwidth">
          <select v-model="newField.value_type">
            <option value="mapping">{{ $t('apiChannel.mapping') }}</option>
            <option value="constant">{{ $t('apiChannel.constant') }}</option>
          </select>
        </span>
      </p>
      <p class="control is-expanded">
        <template v-if="newField.value_type === 'mapping'">
          <span class="select is-small is-fullwidth">
            <select v-model="newField.source_field">
              <option disabled value="">{{ $t('apiChannel.selectField') }}</option>
              <option v-for="field in indexFields" :key="field" :value="field">
                {{ field }}
              </option>
            </select>
          </span>
        </template>
        <template v-else>
          <div class="field has-addons">
            <p class="control">
              <span class="select is-small">
                <select v-model="newField.constant_type">
                  <option value="string">String</option>
                  <option value="number">Number</option>
                  <option value="boolean">Boolean</option>
                  <option value="null">Null</option>
                </select>
              </span>
            </p>
            <p class="control is-expanded">
              <template v-if="newField.constant_type === 'boolean'">
                <span class="select is-small is-fullwidth">
                  <select v-model="newField.constant_value">
                    <option :value="true">true</option>
                    <option :value="false">false</option>
                  </select>
                </span>
              </template>
              <template v-else-if="newField.constant_type === 'null'">
                <input class="input is-small" type="text" value="null" disabled>
              </template>
              <template v-else-if="newField.constant_type === 'number'">
                <input
                  v-model.number="newField.constant_value"
                  class="input is-small"
                  type="number"
                  :placeholder="$t('value')"
                >
              </template>
              <template v-else>
                <input
                  v-model="newField.constant_value"
                  class="input is-small"
                  type="text"
                  :placeholder="$t('value')"
                >
              </template>
            </p>
          </div>
        </template>
      </p>
      <p class="control">
        <button type="button" class="button is-info is-small" @click="addField">
          {{ $t('add') }}
        </button>
      </p>
    </div>

    <!-- Existing fields list -->
    <div class="payload-fields">
      <payload-field-row
        v-for="(field, index) in fields"
        :key="index"
        :field="field"
        :index-fields="indexFields"
        @update="updateField(index, $event)"
        @remove="removeField(index)"
      />
    </div>

    <div v-if="validationErrors.length > 0" class="notification is-danger is-light">
      <ul>
        <li v-for="(error, index) in validationErrors" :key="index">{{ error }}</li>
      </ul>
    </div>

    <div class="field">
      <label class="label">{{ $t('apiChannel.payloadPreview') }}</label>
      <div class="control">
        <monaco-editor
          :value="jsonPreview"
          :generic="true"
          :read-only="true"
          height="150px"
        />
      </div>
    </div>
  </div>
</template>

<script>
import PayloadFieldRow from './PayloadFieldRow.vue'
import MonacoEditor from '@/components/MonacoEditor'

export default {
  name: 'PayloadMapping',
  components: { PayloadFieldRow, MonacoEditor },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    indexFields: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      fields: [...this.value],
      internalUpdate: false,
      newField: this.getEmptyField()
    }
  },
  computed: {
    jsonPreview () {
      const preview = {}
      for (const field of this.fields) {
        if (!field.key) continue
        if (field.value_type === 'mapping') {
          preview[field.key] = `\${${field.source_field || '...'}}`
        } else {
          if (field.constant_type === 'null') {
            preview[field.key] = null
          } else {
            preview[field.key] = field.constant_value
          }
        }
      }
      return JSON.stringify(preview, null, 2)
    },
    validationErrors () {
      const errors = []
      const keys = new Set()

      for (const field of this.fields) {
        if (!field.key) {
          errors.push(this.$t('apiChannel.errorKeyEmpty'))
        } else if (keys.has(field.key)) {
          errors.push(this.$t('apiChannel.errorDuplicateKey', { key: field.key }))
        } else {
          keys.add(field.key)
        }

        if (field.value_type === 'mapping' && !field.source_field) {
          errors.push(this.$t('apiChannel.errorSourceFieldRequired', { key: field.key }))
        }
      }

      return errors
    }
  },
  watch: {
    value: {
      handler (newVal) {
        if (!this.internalUpdate) {
          this.fields = [...newVal]
        }
      },
      deep: true
    },
    fields: {
      handler () {
        this.internalUpdate = true
        this.$emit('input', this.fields)
        this.$nextTick(() => {
          this.internalUpdate = false
        })
      },
      deep: true
    }
  },
  methods: {
    getEmptyField () {
      return {
        key: '',
        value_type: 'mapping',
        source_field: '',
        constant_value: '',
        constant_type: 'string'
      }
    },
    addField () {
      if (!this.newField.key) return
      if (this.newField.constant_type === 'null') {
        this.newField.constant_value = null
      }
      this.fields.push({ ...this.newField })
      this.newField = this.getEmptyField()
    },
    updateField (index, field) {
      this.$set(this.fields, index, field)
    },
    removeField (index) {
      this.fields.splice(index, 1)
    }
  }
}
</script>

<style scoped>
.payload-mapping {
  margin-top: 1rem;
}
.payload-fields {
  margin-bottom: 1rem;
}
.payload-field-row {
  margin-bottom: 0.5rem;
}
</style>
